import "@gds/web-components";
import { useApi } from "../api";
import React, { useState, useEffect, useRef } from "react";
import { Alert, Button } from "../components";
import { usePageLoadingContext } from "../contexts/PageLoadingContext";
import { API_ERROR_RESPONSE } from "../api/types/api-error";
import { API_ERROR_MESSAGE } from "../api/launchpad-api";
import { useAuthContext } from "../contexts/AuthContextProvider";
import { UpsertPartnerQuoteInfoRequest, InitialUpsertQuoteInputs, UpsertInitialInputErrorMessages } from "../api/types/upsert-partner-quote-info-request";
import { UPSERT_PARNTER_QUOTE_INFO_RESPONSE_FAILURE } from "../api/types/upsert-partner-quote-info-response";
import { STATE_LOOKUP_RESPONSE, StateLookupRequest } from "../api/types";
import { getLobNamesForAgents, getEndorsementLobNamesForAgents } from '../config/product-config';
import { PARTNER_DETAILS_RESPONSE } from "../api/types/partner-details";
import InputMask from 'react-input-mask';
import QuoteOutcome from "./quote-outcome";
import QuoteOutcomeFields from "./quote-outcome-fields";
import { getLanguagesForAgents, getLeadSourcesForAgents, getTypeOfQuote, getTypeOfSecondaryQuote } from "../config/quote-outcomes-config";
import { convertToQuoteOutcomeRequest, defaultQuoteOutcomeHighlightMap } from "../api/types/quote-outcome";
import { useSelector } from "react-redux";
import store, { RootState } from "../store";
import { getQuoteAggregatorNames } from "../config/partner-config/quote-aggregators";
import { alertMessagesConfig } from "../config/quote-outcome/alertMessagesConfig";
import { generateNotes } from "../config/quote-outcome/GenerateNotes";
import { updateQuote } from "../store/quote/action";
import { isEmailValid } from "../lib/text-helpers";
import { QuoteInfo } from "../api/types/search-quote-response";
import { mapQuoteInfoToUpsertInputs } from '../config/quote-outcome/prefill-mapper';
import { validatePolicyNumberForSource } from "../config/quote-outcome/policyNumberValidation";
import { useClientConfigs } from "../contexts/ClientConfigContext";

interface AgentConsolidatedQuoteInfoProps {
    isExpandedMore: boolean;
    prefillData?: QuoteInfo;
    setActivatePrefillHandler?: (handler: () => void) => void;
}

export const AgentConsolidatedQuoteInfo: React.FC<AgentConsolidatedQuoteInfoProps> = ({ isExpandedMore, prefillData, setActivatePrefillHandler }) => {
    const api = useApi();
    const authContext = useAuthContext();
    const clientConfig = useClientConfigs();
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState<{ title: string; message: React.ReactNode }>({ title: "", message: "" });
    const [alertMessageDisclosure, setAlertMessageDisclosure] = useState<{ title: string; message: React.ReactNode }>({ title: "", message: "" });
    const { showLoader, hideLoader } = usePageLoadingContext();
    const [validationErrors, setValidationErrors] = useState(UpsertInitialInputErrorMessages);
    const [upsertQuoteInputs, setUpsertQuoteInputs] = useState<UpsertPartnerQuoteInfoRequest>(InitialUpsertQuoteInputs);
    const [carrierCodes, setCarrierCodes] = useState<{ value: string, label: string }[]>([]);
    const [successMessage, setSuccessMessage] = useState('');
    const [lobNames, setLobNames] = useState<string[]>([]);
    const [endorsementLobNames, setEndorsementLobNames] = useState<string[]>([]);
    const [selectedOutcome, setSelectedOutcome] = useState<string>('');
    const [leadSources, setLeadSources] = useState<string[]>([]);
    const [languages, setLanguages] = useState<string[]>([]);
    const [requiredFields, setRequiredFields] = useState<string[]>(["quoteOutcome"]);
    const quote = useSelector((state: RootState) => state.quote.quote);
    const [showRedirectMessage, setShowRedirectMessage] = useState(false);
    const [redirectAnswer, setRedirectAnswer] = useState(false);
    const quoteAggregatorNames = getQuoteAggregatorNames();
    const [isFixedSource, setIsFixedSource] = useState(false);
    const previousLaunchpadId = useRef<string | null>(null);
    const [typeOfQuote, setTypeOfQuote] = useState<string[]>([]);
    const [subtype, setSubtype] = useState<string[]>([]);
    const [shouldTypeOfQuoteDropwdownsShow, setShouldTypeOfQuoteDropwdownsShow] = useState<boolean>(false);
    const [isRPMAuthorization, setIsRPMAuthorization] = useState<boolean>(false);
    const [manualNotes, setManualNotes] = useState<string>("");
    const [additionalNotes, setAdditionalNotes] = useState<string>("");
    const [disclosureNotes, setDisclosureNotes] = useState<string>("");
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [highlightedFields, setHighlightedFields] = useState<{ [key: string]: boolean }>(defaultQuoteOutcomeHighlightMap);
    const [isPrefillMode, setIsPrefillMode] = useState(false);
    const [endorsements, setEndorsements] = useState<{ id: string; lob: string; policyNumber: string; status: string; primaryOutcomeType?: string }[]>([]);
    const [hasEndorsements, setHasEndorsements] = useState<boolean>(false);

    useEffect(() => {
        const isIncompleteOrDecline = upsertQuoteInputs.quoteOutcome === "Not Quoted" &&
            (upsertQuoteInputs.primaryOutcomeType === "Misdirect" || upsertQuoteInputs.primaryOutcomeType === "Decline");

        api.commercialAgentApi.getCarrierCodes(isIncompleteOrDecline)
            .then(response => {
                if (response.type === PARTNER_DETAILS_RESPONSE) {
                    const options = response.data.map(carrier => ({
                        value: carrier.code,
                        label: carrier.name
                    }));
                    setCarrierCodes(options);
                } else {
                    console.error("Error fetching carrier codes:", response);
                }
            })
            .catch(error => {
                console.error("Error fetching carrier codes:", error);
            });
    }, [upsertQuoteInputs.quoteOutcome, upsertQuoteInputs.primaryOutcomeType]);

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage('');
            }, 3000); // Remove the success message after 3 seconds
            return () => clearTimeout(timer); // Cleanup the timer on component unmount
        }
    }, [successMessage]);

    useEffect(() => {
        setIsRPMAuthorization(upsertQuoteInputs.disclosuresSelectedAsYes.includes("RPM Authorization"))
        const yesDisclosures = upsertQuoteInputs.disclosuresSelectedAsYes.length > 0
            ? upsertQuoteInputs.disclosuresSelectedAsYes
                .map((disclosure) => `Read ${disclosure}: Yes`)
                .join("\n")
            : "";
        const noDisclosures = upsertQuoteInputs.disclosuresSelectedAsNo.length > 0
            ? upsertQuoteInputs.disclosuresSelectedAsNo
                .map((disclosure) => `Read ${disclosure}: No`)
                .join("\n")
            : "";
        const naDisclosures = upsertQuoteInputs.disclosuresSelectedAsNA.length > 0
            ? upsertQuoteInputs.disclosuresSelectedAsNA
                .map((disclosure) => `Read ${disclosure}: N/A`)
                .join("\n")
            : "";
        setDisclosureNotes(
            `${yesDisclosures}${yesDisclosures && noDisclosures ? "\n" : ""}${noDisclosures}${(yesDisclosures || noDisclosures) && naDisclosures ? "\n" : ""}${naDisclosures}`
        );

    }, [upsertQuoteInputs.disclosuresRead,
    upsertQuoteInputs.disclosuresSelectedAsYes,
    upsertQuoteInputs.disclosuresSelectedAsNo,
    upsertQuoteInputs.disclosuresSelectedAsNA]);

    useEffect(() => {
        const notes = generateNotes(upsertQuoteInputs);
        setAdditionalNotes(notes);
    }, [
        upsertQuoteInputs.lineOfBusiness,
        upsertQuoteInputs.quoteOutcome,
        upsertQuoteInputs.source,
        upsertQuoteInputs.primaryOutcomeType,
        upsertQuoteInputs.firstName,
        upsertQuoteInputs.lastName,
        upsertQuoteInputs.referenceNumber,
        upsertQuoteInputs.policyNumber,
        upsertQuoteInputs.soldPremium,
        upsertQuoteInputs.paymentConfirmationNumber,
        upsertQuoteInputs.businessClassDescription,
    ]);

    const autoNotes = [disclosureNotes, additionalNotes].filter(Boolean).join("\n");
    const agentNotes = [manualNotes, autoNotes].filter(Boolean).join("\n");

    useEffect(() => {
        const messageList = alertMessagesConfig
            .filter(({ applicableTo }) => {
                const matchesLineOfBusiness =
                    applicableTo.lineOfBusiness.length === 0 ||
                    applicableTo.lineOfBusiness.includes(upsertQuoteInputs.lineOfBusiness);

                const matchesQuoteOutcome =
                    applicableTo.quoteOutcome.length === 0 ||
                    applicableTo.quoteOutcome.includes(upsertQuoteInputs.quoteOutcome);

                const matchesSource =
                    applicableTo.source.length === 0 ||
                    applicableTo.source.includes(upsertQuoteInputs.source);

                return matchesLineOfBusiness && matchesQuoteOutcome && matchesSource;
            })
            .flatMap(({ messages }) => messages);

        setAlertMessageDisclosure({
            title: "Risk Details",
            message: (
                <div>
                    <div>Document the following:</div>
                    <div className="ul-alert">
                        {messageList.map((message, index) => (
                            <div key={index} className="li-alert">
                                {message}
                            </div>
                        ))}
                    </div>
                </div>
            )
        });
    }, [upsertQuoteInputs.lineOfBusiness, upsertQuoteInputs.quoteOutcome, upsertQuoteInputs.source]);

    useEffect(() => {
        const lobNames = getLobNamesForAgents();
        setLobNames(lobNames);

        const endorsementLobs = getEndorsementLobNamesForAgents();
        setEndorsementLobNames(endorsementLobs);

        const leadSources = getLeadSourcesForAgents();
        setLeadSources(leadSources);

        const languages = getLanguagesForAgents();
        setLanguages(languages);

        const typeOfSecondaryQuote = getTypeOfSecondaryQuote();
        setSubtype(typeOfSecondaryQuote);
    }, []);

    useEffect(() => {
        setHighlightedFields(defaultQuoteOutcomeHighlightMap);
        const typeOfQuote = getTypeOfQuote(selectedOutcome);
        setTypeOfQuote(typeOfQuote);
        if (selectedOutcome) {
            setShouldTypeOfQuoteDropwdownsShow(true)
        }

        if (selectedOutcome == "Quote") {
            setUpsertQuoteInputs(prev => ({
                ...prev,
                primaryOutcomeType: typeOfQuote[0] ?? "",
            }));
        }
    }, [selectedOutcome]);

    useEffect(() => {
        if (hasSubmitted) {
            setHasSubmitted(false)
            return;
        }

        if (quote.data.CarrierDetail && quote.data.CarrierDetail.length > 0 && quote.data.CarrierDetail[0].CarrierCob != null) {
            const carrierName = quote.data.CarrierDetail[0].CarrierName;
            const isCarrierValid = carrierCodes.some(carrier => {
                const splitCarrierValues = carrier.value.split(/[/_]/);
                return splitCarrierValues.some(splitValue => carrierName.includes(splitValue));
            });
            const isValidAggregator = quoteAggregatorNames.includes(carrierName);
            if (isCarrierValid) {
                setIsFixedSource(true);
                setShowRedirectMessage(true);
            }
            else if (isValidAggregator) {
                setIsFixedSource(false);
                setShowRedirectMessage(true);
            }
            else {
                setRedirectAnswer(false);
                setShowRedirectMessage(false);
            }
        } else {
            setRedirectAnswer(false);
            setShowRedirectMessage(false);
        }
    }, [quote.data.CarrierDetail, carrierCodes]);

    useEffect(() => {
        resetForm();
        setShowRedirectMessage(false);
        setRedirectAnswer(false);
        if (previousLaunchpadId.current !== quote.data.LaunchpadId && quote.data.LaunchpadId) {
            quote.data.CarrierDetail && quote.data.CarrierDetail.length > 0 && quote.data.CarrierDetail[0].CarrierCode != undefined ? setShowRedirectMessage(true) : setShowRedirectMessage(false);
            previousLaunchpadId.current = quote.data.LaunchpadId;
        } else if (quote.data.CarrierDetail && quote.data.CarrierDetail[0]?.CarrierCob === "Redirect-CAU") {
            setShowRedirectMessage(true)
            let finalQuote = {
                ...quote.data,
                CarrierDetail: quote.data.CarrierDetail.map(carrier => ({
                    ...carrier,
                    CarrierCob: ""
                }))
            }
            store.dispatch(updateQuote(finalQuote));
        }

    }, [quote.data.SelectedProducts, quote.data.LaunchpadId]);

    useEffect(() => {
        if (redirectAnswer === true) {
            if (quote.data.SelectedProducts?.length > 0) {
                setUpsertQuoteInputs(prevState => ({
                    ...prevState,
                    lineOfBusiness: quote.data.SelectedProducts[0].LobName
                }));
            }
            if (quote.data.CarrierDetail && quote.data.CarrierDetail?.length > 0) {
                setUpsertQuoteInputs(prevState => ({
                    ...prevState,
                    source: quote.data.CarrierDetail ? quote.data.CarrierDetail[0].CarrierName : ""
                }));
            }
            if (quote.data.Zip && parseInt(quote.data.Zip) > 0) {
                setUpsertQuoteInputs(prevState => ({
                    ...prevState,
                    zipCode: quote.data.Zip
                }));

                setValidationErrors(prevState => ({
                    ...prevState,
                    zipCode: "",
                    stateCode: ""
                }));

                stateLookup(quote.data.Zip, "");
            }
        }
    }, [redirectAnswer, quote.data.SelectedProducts, quote.data.CarrierDetail]);

    useEffect(() => {
        if (!Object.values(highlightedFields).some(Boolean)) {
            clearAlertMessage();
        }
    }, [highlightedFields])

    useEffect(() => {
        setUpsertQuoteInputs(InitialUpsertQuoteInputs());
        setSelectedOutcome("");
        setIsPrefillMode(false);
        // ...reset other related state as needed
    }, [prefillData]);

    // Prefill blank fields whenever prefill mode is active and prefillData exists
    useEffect(() => {
        if (isPrefillMode && prefillData) {
            if (prefillData.quoteOutcome?.quoteOutcomeType) {
                setSelectedOutcome(prefillData.quoteOutcome.quoteOutcomeType);
            }

            const timer = setTimeout(() => {
                setUpsertQuoteInputs(prev => {
                    const mapped = mapQuoteInfoToUpsertInputs(prefillData);
                    return { ...prev, ...mapped };
                });
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [isPrefillMode, prefillData]);

    // Expose the handler to parent on mount
    useEffect(() => {
        if (setActivatePrefillHandler) {
            setActivatePrefillHandler(handleActivatePrefill);
        }
        return () => {
            if (setActivatePrefillHandler) {
                setActivatePrefillHandler(() => { });
            }
        };
    }, [setActivatePrefillHandler]);

    // Reset form when prefillData changes (quote changes)
    useEffect(() => {
        setUpsertQuoteInputs(InitialUpsertQuoteInputs());
        resetForm();
    }, [prefillData]);

    // Handler to activate prefill mode (to be called by parent)
    const handleActivatePrefill = () => {
        setIsPrefillMode(true);
    };

    // mapQuoteInfoToUpsertInputs is imported from prefill-mapper
    function generateTimestampBasedId(): string {
        const timestamp = Date.now().toString(36);
        const randomPart = Math.random().toString(36).substring(2, 10);
        return `${timestamp}${randomPart}`;
    }

    async function stateLookup(zipCode: string, message: string) {
        const request: StateLookupRequest = { zipCode };
        showLoader();

        // Clear the stateCode before making the API call
        setUpsertQuoteInputs(prevState => ({
            ...prevState,
            stateCode: ""
        }));

        try {
            const response = await api.commercialAgentApi.stateLookup(request);

            if (response.type !== STATE_LOOKUP_RESPONSE) {
                message = API_ERROR_MESSAGE;
                setValidationErrors(prevState => ({
                    ...prevState,
                    zipCode: message
                }));
            } else if (!response.data.success) {
                message = response.data.errorMessage || "Please enter a valid zip code.";
                setValidationErrors(prevState => ({
                    ...prevState,
                    zipCode: message
                }));
            } else {
                setUpsertQuoteInputs(prevState => ({
                    ...prevState,
                    stateCode: response.data.state
                }));
                setHighlightedFields(prevState => ({
                    ...prevState,
                    stateCode: false
                }));
            }
        } catch (error) {
            console.error(error);
        } finally {
            hideLoader();
        }
    }

    function clearAlertMessage() {
        setAlertMessage({
            title: "",
            message: ""
        });
        setShowAlert(false);
    }

    function focusOrScrollToInvalidField(firstInvalid: string) {
        if (firstInvalid === "quoteOutcome") {
            const groupEl = document.getElementById("quoteOutcome-sidebar");
            if (groupEl) groupEl.scrollIntoView({ behavior: "auto", block: "center" });
        } else if (firstInvalid === "effectiveDate" || firstInvalid === "expirationDate") {
            const el = document.getElementById("lineOfBusiness-sidebar");
            if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
        } else if (firstInvalid === "businessAddress") {
            const el = document.getElementById(firstInvalid + '-sidebar-address-search-field');
            if (el && typeof el.focus === "function") {
                el.focus();
            } else if (el) {
                el.scrollIntoView({ behavior: "auto", block: "center" });
            }
        } else {
            const el = document.getElementById(firstInvalid + "-sidebar");
            if (el && typeof el.focus === "function") {
                el.focus();
            } else if (el) {
                el.scrollIntoView({ behavior: "auto", block: "center" });
            }
        }
    }

    function submitEndorsements(baseQuoteInputs: UpsertPartnerQuoteInfoRequest): void {
        const validEndorsements = endorsements.filter(
            endorsement =>
                endorsement.lob && endorsement.lob.trim() !== "" &&
                endorsement.policyNumber && endorsement.policyNumber.trim() !== "" &&
                endorsement.status && endorsement.status.trim() !== ""
        );

        if (validEndorsements.length === 0) {
            setSuccessMessage("Quote Saved Successfully.");
            clearAlertMessage();
            hideLoader();
            setShowRedirectMessage(false);
            createCopy();
            setRedirectAnswer(false);
            setHasSubmitted(true);
            setSelectedOutcome("");
            setShouldTypeOfQuoteDropwdownsShow(false);
            setHighlightedFields(defaultQuoteOutcomeHighlightMap);
            setRequiredFields(["quoteOutcome"]);
            return;
        }

        const submitPromises = validEndorsements.map((endorsement, index) => {
            const endorsementInputs: UpsertPartnerQuoteInfoRequest = {
                ...baseQuoteInputs,
                lineOfBusiness: endorsement.lob,
                referenceNumber: endorsement.policyNumber.trim() || generateTimestampBasedId(),
                quoteOutcome: endorsement.status,
                isEndorsement: true,
                primaryOutcomeType: endorsement.status === "Quote" && endorsement.primaryOutcomeType ? endorsement.primaryOutcomeType : "",
                secondaryOutcomeType: "",
                policyNumber: "",
                premium: "0",
                soldPremium: "0",
                effectiveDate: "",
                expirationDate: "",
                paymentPlan: "",
                paymentConfirmationNumber: "",
            };

            const endorsementQuoteOutcomeInputs = convertToQuoteOutcomeRequest(endorsementInputs);

            return api.commercialAgentApi.upsertPartnerQuoteInfoWithQuoteOutcome(
                endorsementQuoteOutcomeInputs,
                authContext.getAuthToken()
            ).then((response) => {
                if ((response.type === UPSERT_PARNTER_QUOTE_INFO_RESPONSE_FAILURE || response.type === API_ERROR_RESPONSE)) {
                    api.commercialAgentApi.logError({
                        ErrorMessage: `Error submitting endorsement ${index + 1} of ${validEndorsements.length}`,
                        Title: "Endorsement submission error",
                        ErrorDetails: `Reference Number: ${endorsementInputs.referenceNumber}, LOB: ${endorsement.lob}, Status: ${endorsement.status}`,
                    }).catch(() => { });
                    throw new Error(`Failed to submit endorsement ${index + 1}`);
                }
                return response;
            });
        });

        Promise.all(submitPromises)
            .then(() => {
                setSuccessMessage(`Quote and ${validEndorsements.length} endorsement(s) saved successfully.`);
                clearAlertMessage();
                hideLoader();
                setShowRedirectMessage(false);
                createCopy();
                setRedirectAnswer(false);
                setHasSubmitted(true);
                setSelectedOutcome("");
                setShouldTypeOfQuoteDropwdownsShow(false);
                setHighlightedFields(defaultQuoteOutcomeHighlightMap);
                setRequiredFields(["quoteOutcome"]);
            })
            .catch((error) => {
                setAlertMessage({
                    title: "Error submitting endorsements",
                    message: "Quote was saved successfully, but some endorsements failed to save. Please check the logs."
                });
                setShowAlert(true);
                hideLoader();
                setShowRedirectMessage(false);
                createCopy();
                setRedirectAnswer(false);
                setHasSubmitted(true);
                setSelectedOutcome("");
                setShouldTypeOfQuoteDropwdownsShow(false);
                setHighlightedFields(defaultQuoteOutcomeHighlightMap);
                setRequiredFields(["quoteOutcome"]);
            });
    }

    function submitForm(this: any): void {
        clearAlertMessage();
        showLoader();

        const highlightedFields = validateAndHighlightFields();
        const firstInvalid = Object.keys(highlightedFields).find(k => highlightedFields[k]);
        if (firstInvalid) {
            setAlertMessage({
                title: "Invalid Submission",
                message: "Please provide valid information according to the submission rules."
            });
            setShowAlert(true);
            focusOrScrollToInvalidField(firstInvalid);

            const errorLogDetails = [
                `Save Quote Request failed on submission validation`,
                `Reference Number: ${upsertQuoteInputs.referenceNumber || "(none)"}`,
                `selectedOutcome: ${selectedOutcome}`,
                `validationErrors: ${JSON.stringify(validationErrors)}`,
                `requiredFields: ${JSON.stringify(requiredFields)}`,
                `highlightedFields: ${JSON.stringify(highlightedFields)}`,
                `redirectAnswer: ${JSON.stringify(redirectAnswer)}`, ,
            ].join(' ');

            api.commercialAgentApi.logError({
                ErrorMessage: "Error while upserting partner quote info - there are errors in the form",
                Title: "Form validation errors when trying to submit quote",
                ErrorDetails: errorLogDetails,
            })
                .then(() => { })
                .catch((error) => { });
            hideLoader();
            return;
        }

        if (upsertQuoteInputs.premium === null || upsertQuoteInputs.premium === "") {
            upsertQuoteInputs.premium = "0";
        }
        if (upsertQuoteInputs.soldPremium === null || upsertQuoteInputs.soldPremium === "") {
            upsertQuoteInputs.soldPremium = "0";
        }

        const cleanedPhoneNumber = upsertQuoteInputs.phoneNumber.replace(/[-\s()]/g, '');
        const updatedPremium = parseFloat(upsertQuoteInputs.premium).toFixed(2);
        const updatedSoldPremium = parseFloat(upsertQuoteInputs.soldPremium).toFixed(2);
        const isNotQuoted = selectedOutcome === "Not Quoted";
        const updatedQuoteInputs: UpsertPartnerQuoteInfoRequest = {
            ...upsertQuoteInputs,
            zipCode: upsertQuoteInputs.zipCode?.trim() === "" && isNotQuoted ? "99999" : upsertQuoteInputs.zipCode,
            stateCode: upsertQuoteInputs.stateCode?.trim() === "" && isNotQuoted ? "NA" : upsertQuoteInputs.stateCode,
            emailId: upsertQuoteInputs.emailId?.trim() === "" && isNotQuoted ? "Not Applicable" : upsertQuoteInputs.emailId,
            lastName: upsertQuoteInputs.lastName?.trim() === "" && isNotQuoted ? "Not Applicable" : upsertQuoteInputs.lastName,
            businessAddress: upsertQuoteInputs.businessAddress?.trim() === "" && isNotQuoted ? "Not Applicable" : upsertQuoteInputs.businessAddress,
            referenceNumber: upsertQuoteInputs.referenceNumber?.trim() === "" && isNotQuoted ? generateTimestampBasedId() : upsertQuoteInputs.referenceNumber,
            source: upsertQuoteInputs.source?.trim() === "" && isNotQuoted ? "Not Applicable" : upsertQuoteInputs.source,
            leadSource: upsertQuoteInputs.leadSource || "N/A",
            phoneNumber: cleanedPhoneNumber,
            premium: updatedPremium,
            soldPremium: updatedSoldPremium,
            userType: authContext.getUserType(),
            agentNotes: manualNotes,
            disclosureNotes: autoNotes,
            agentId: authContext.getEmployeeId(),
            agentName: authContext.getUserName()?.toString() ?? "",
            callReason: "1",
            createdDate: new Date().toISOString(),
            quoteCreatedDate: new Date().toISOString(),
            quoteUpdatedDate: new Date().toISOString(),
            correlationId: redirectAnswer === true ? quote.data.LaunchpadId : "",
            isEndorsement: prefillData?.quoteOutcome?.isEndorsement === true || upsertQuoteInputs.isEndorsement === true
        };
        const quoteOutcomeInputs = convertToQuoteOutcomeRequest(updatedQuoteInputs);
        api.commercialAgentApi.logError({
            ErrorMessage: `Save Quote request sent with Reference Number: ${updatedQuoteInputs.referenceNumber}`,
            Title: "Upsert Partner Quote Request sending",
            ErrorDetails: '',
        })
            .then(() => { })
            .catch((error) => { });
        api.commercialAgentApi.upsertPartnerQuoteInfoWithQuoteOutcome(quoteOutcomeInputs, authContext.getAuthToken())
            .then((response) => {
                if ((response.type === UPSERT_PARNTER_QUOTE_INFO_RESPONSE_FAILURE || response.type === API_ERROR_RESPONSE)) {
                    let errorMessage = 'Oops... Unable to Save Partner Quote info at this time, Please try again later';

                    if (response.type === API_ERROR_RESPONSE && response.errors) {
                        const errorMessages = Object.values(response.errors).flat().join(", ");
                        errorMessage = `Unable to add quote: ${errorMessages}`;
                    }

                    if (response.type === API_ERROR_RESPONSE && response.statusCode === 401) {
                        errorMessage = "Your session has expired. Please refresh the page and try again.";
                    }

                    setAlertMessage({
                        title: "Unable to add quote",
                        message: errorMessage
                    });
                    setShowAlert(true);
                    const errorLogDetails = [
                        `Save Quote Request failed`,
                        `Reference Number: ${updatedQuoteInputs.referenceNumber || "(none)"}`,
                        response.type === API_ERROR_RESPONSE
                            ? `Response Data: ${JSON.stringify(response.data)}`
                            : `Response Type: ${response.type}`,
                        response.type === API_ERROR_RESPONSE
                            ? `Status Code: ${response.statusCode}`
                            : "",
                        response.type === API_ERROR_RESPONSE
                            ? `Status Message: ${response.message} Status Text: ${response.statusText}`
                            : "",
                        `Errors: ${response.type === UPSERT_PARNTER_QUOTE_INFO_RESPONSE_FAILURE ? JSON.stringify(response.data?.notices?.[0]?.message) : JSON.stringify(response.errors)}`,
                        `selectedOutcome: ${selectedOutcome}`,
                        `validationErrors: ${JSON.stringify(validationErrors)}`,
                        `requiredFields: ${JSON.stringify(requiredFields)}`,
                        `redirectAnswer: ${JSON.stringify(redirectAnswer)}`,
                        `alertMessage: ${JSON.stringify(errorMessage)}`,
                    ].join(' ');

                    api.commercialAgentApi.logError({
                        ErrorMessage: "Error while upserting partner quote info - response not expected type",
                        Title: "API Error when upserting partner quote info",
                        ErrorDetails: errorLogDetails,
                    })
                        .then(() => { })
                        .catch((error) => { });
                    hideLoader();
                    return;
                }

                // If main quote is saved successfully and there are endorsements, submit them
                // Skip submitting additional endorsements if the main quote itself is an endorsement
                const isMainQuoteEndorsement = prefillData?.quoteOutcome?.isEndorsement === true || upsertQuoteInputs.isEndorsement === true;
                if (!isMainQuoteEndorsement && hasEndorsements && endorsements.length > 0) {
                    submitEndorsements(updatedQuoteInputs);
                } else {
                    setSuccessMessage("Quote Saved Successfully.");
                    clearAlertMessage();
                    hideLoader();
                    setShowRedirectMessage(false);
                    createCopy();
                    setRedirectAnswer(false);
                    setHasSubmitted(true);
                    setSelectedOutcome("");
                    setShouldTypeOfQuoteDropwdownsShow(false);
                    setHighlightedFields(defaultQuoteOutcomeHighlightMap);
                    setRequiredFields(["quoteOutcome"]);
                }
            });
    }

    function validateAndHighlightFields() {
        const newHighlightedFields: { [key: string]: boolean } = { ...highlightedFields };

        for (const field of requiredFields) {
            const fieldValue = field === "leadSource"
                ? (upsertQuoteInputs.leadSource || "N/A")
                : upsertQuoteInputs[field as keyof UpsertPartnerQuoteInfoRequest];
            newHighlightedFields[field] = !fieldValue;
            if (selectedOutcome === "Not Quoted" && upsertQuoteInputs.primaryOutcomeType === "Decline" && field === "agentNotes") {
                newHighlightedFields[field] = !agentNotes || agentNotes.trim() === "";
            }
        }

        Object.entries(validationErrors).forEach(([field, error]) => {
            if (error) newHighlightedFields[field] = true;
        });

        setHighlightedFields(newHighlightedFields);
        return newHighlightedFields;
    }

    const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        let errorMessage = "";
        const nextSourceValue = name === "source" ? value : upsertQuoteInputs.source;
        const nextReferenceNumberValue = name === "referenceNumber" ? value : upsertQuoteInputs.referenceNumber;

        // Define validation logic for each input
        if (name === "dateOfBirth") {
            const dobRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d{2}$/;
            if ((/\d/.test(value)) && (value.length < 10 || !dobRegex.test(value))) {
                errorMessage = "Please enter date as MM/DD/YYYY";
            }
        } else if (name === "effectiveDate" || name === "expirationDate") {
            const dobRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d{2}$/;
            const [month, day, year] = value.split('/').map(Number);
            const inputDate = new Date(year, month - 1, day);
            const today = new Date();
            const twoYearsAgo = new Date();
            twoYearsAgo.setFullYear(today.getFullYear() - 2, today.getMonth(), today.getDate() - 1);
            const twoYearsFromNow = new Date();
            twoYearsFromNow.setFullYear(today.getFullYear() + 2);
            if ((/\d/.test(value)) && (value.length < 10 || !dobRegex.test(value))) {
                errorMessage = "Please enter date as MM/DD/YYYY";
            }
            else if (inputDate < twoYearsAgo || inputDate > twoYearsFromNow) {
                errorMessage = "Please enter a date within the last 2 years or the next 2 years.";
            }
        } else if (name === "emailId") {
            if (value.length !== 0 && !isEmailValid(value)) {
                errorMessage = "Please enter email address in this format: Name@service.com";
            }
        } else if (name === "premium") {
            const premiumRegex = /^\d+(\.\d{1,2})?$/; // Currency format: digits with optional two decimal places
            if (value.trim() === "") {
                errorMessage = "Premium is required.";
            } else if (!premiumRegex.test(value)) {
                errorMessage = "Please enter a valid premium amount (e.g., 1000 or 1000.00).";
            }
        } else if (name === "soldPremium") {
            const premiumRegex = /^\d+(\.\d{1,2})?$/; // Currency format: digits with optional two decimal places
            if (value.trim() === "") {
                errorMessage = "Sold Premium is required.";
            } else if (!premiumRegex.test(value)) {
                errorMessage = "Please enter a valid sold premium amount (e.g., 1000 or 1000.00).";
            }
        } else if (name === "phoneNumber") {
            const unmaskedValue = value.replace(/\D/g, '');
            const phoneRegex = /^[0-9]{10}$/;
            if ((/\d/.test(value)) && (unmaskedValue.length < 10 || !phoneRegex.test(unmaskedValue))) {
                errorMessage = "Oops, it looks like you may need to enter a valid phone number in the format (999) 999-9999.";
            } else {
                errorMessage = "";
            }
        } else if (value.length !== 0 && name === "zipCode") {
            if (value.length < 5) {
                errorMessage = "Please enter a valid zip code.";
            } else {
                stateLookup(value, errorMessage);
            }
            if (errorMessage) {
                upsertQuoteInputs.stateCode = "";
            }
        } else if (name === "referenceNumber") {
            if (clientConfig.isCommercialLaunchpadExternalConfigurationEnabled) {
                const referenceNumberRegex = /^[a-zA-Z0-9*-.]{1,50}$/;
                if (selectedOutcome === "Sale" && !upsertQuoteInputs.isEndorsement) {
                    const { error } = validatePolicyNumberForSource(value, nextSourceValue);
                    errorMessage = error;
                } else if ((value.length !== 0) && (value.length > 50 || !referenceNumberRegex.test(value))) {
                    errorMessage = "Please enter a valid reference number. A-Z, a-z, 0-9, *, -, and . are the only characters allowed";
                }
            }
        }
        setValidationErrors(prev => {
            const updatedErrors = {
                ...prev,
                [name]: errorMessage
            };

            if (clientConfig.isCommercialLaunchpadExternalConfigurationEnabled && selectedOutcome === "Sale" && !upsertQuoteInputs.isEndorsement && (name === "source" || name === "referenceNumber")) {
                const { error } = validatePolicyNumberForSource(nextReferenceNumberValue, nextSourceValue);
                updatedErrors.referenceNumber = error;
            }

            return updatedErrors;
        });

        setUpsertQuoteInputs(prevState => ({
            ...prevState,
            [name]: value
        }));

        setHighlightedFields(prev => {
            const updatedHighlights = {
                ...prev,
                [name]: !!errorMessage || (requiredFields.includes(name) && value.trim() === "")
            };

            if (clientConfig.isCommercialLaunchpadExternalConfigurationEnabled && selectedOutcome === "Sale" && !upsertQuoteInputs.isEndorsement && (name === "source" || name === "referenceNumber")) {
                const { error } = validatePolicyNumberForSource(nextReferenceNumberValue, nextSourceValue);
                updatedHighlights.referenceNumber = !!error || (requiredFields.includes("referenceNumber") && nextReferenceNumberValue.trim() === "");
            }

            return updatedHighlights;
        });
    };


    const [disclosuresCopied, setDisclosuresCopied] = useState(false);

    const handleCopyDisclosures = () => {
        if (!autoNotes) {
            return;
        }

        navigator.clipboard
            .writeText(autoNotes)
            .then(() => {
                setDisclosuresCopied(true);
                setTimeout(() => setDisclosuresCopied(false), 2000);
            })
            .catch(() => {});
    };

    const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setManualNotes(e.target.value);
        setHighlightedFields(prev => ({
            ...prev,
            agentNotes: (requiredFields.includes("agentNotes") && e.target.value.trim() === "")
        }));
    };

    function resetForm(): void {
        setUpsertQuoteInputs(InitialUpsertQuoteInputs());
        setSelectedOutcome("");
        setShouldTypeOfQuoteDropwdownsShow(false)
        setShowAlert(false);
        setValidationErrors(UpsertInitialInputErrorMessages());
        setRequiredFields(["quoteOutcome"]);
        setHighlightedFields(defaultQuoteOutcomeHighlightMap);
        setIsPrefillMode(false);
        clearAlertMessage();
        setManualNotes("");
        setAdditionalNotes("");
        setDisclosureNotes("");
    }

    type UpsertPartnerQuoteInfoRequestStrings = {
        [K in keyof UpsertPartnerQuoteInfoRequest as UpsertPartnerQuoteInfoRequest[K] extends string ? K : never]: string;
    };

    type UpsertPartnerQuoteInfoRequestStringArrays = {
        [K in keyof UpsertPartnerQuoteInfoRequest as UpsertPartnerQuoteInfoRequest[K] extends string[] ? K : never]: string[];
    };

    function resetQuoteOutcomeFields(fields: string[]): void {
        const resetFields = <T extends Record<string, any>>(state: T) => {
            const updatedState = { ...state };
            fields.forEach((field) => {
                if (field in (state as unknown as UpsertPartnerQuoteInfoRequestStrings)) {
                    (updatedState as Record<string, any>)[field] = "";
                } else if (field in (state as unknown as UpsertPartnerQuoteInfoRequestStringArrays)) {
                    (updatedState as Record<string, any>)[field] = [];
                }
            });
            return updatedState;
        };

        setUpsertQuoteInputs((prevState) => resetFields(prevState));
        setValidationErrors((prevState) => resetFields(prevState));
    }

    function createCopy(): void {
        setSelectedOutcome("");
        setIsPrefillMode(false);
        setShouldTypeOfQuoteDropwdownsShow(false);
        setUpsertQuoteInputs(prevState => ({
            ...prevState,
            source: "",
            lineOfBusiness: "",
            referenceNumber: "",
            premium: "",
            agentNotes: "",
            quoteOutcome: "",
            primaryOutcomeType: "",
            secondaryOutcomeType: "",
        }));
        setRequiredFields(["quoteOutcome"]);
        setHighlightedFields(defaultQuoteOutcomeHighlightMap);
    }

    return (
        <div>
            <h3>Final Outcome Info</h3>
            {showRedirectMessage && (
                <div>
                    <h4>
                        <span className="notify-text">
                            You have redirected to
                        </span>
                        <span className="notify-text-values">
                            {quote.data?.CarrierDetail?.[0]?.CarrierName ?? ""}
                        </span>
                        <span className="notify-text">
                            {(quote.data.BusinessKeyword?.description || quote.data.BusinessSecondaryKeyword?.description)
                                ? "for the following Business Insurance Quote:"
                                : "for Quoting a customer"}
                        </span>
                        <span className="notify-text-values">
                            {(quote.data.BusinessKeyword?.description || quote.data.BusinessSecondaryKeyword?.description)
                                ? `${quote.data.BusinessKeyword?.description} ${quote.data.BusinessKeyword?.description !== quote.data.BusinessSecondaryKeyword?.description ? quote.data.BusinessSecondaryKeyword?.description : ""}`
                                : ""}
                        </span>
                    </h4>
                    <form>
                        <fieldset role="radiogroup" className="form-field radio-button-group" style={{ display: "flex" }}>
                            <legend>Are you filling the Partner Quote info for this redirect?</legend>
                            <div className="" style={{ display: "flex", minWidth: "100%" }}>
                                <input
                                    id="radio-yes"
                                    type="radio"
                                    name="redirectAnswer"
                                    value="yes"
                                    checked={redirectAnswer === true}
                                    onChange={() => setRedirectAnswer(true)}
                                />
                                <label className="radio" htmlFor="radio-yes" style={{ width: "50%", margin: "4px" }}>
                                    <span>Yes</span>
                                </label>
                                <input
                                    id="radio-no"
                                    type="radio"
                                    name="redirectAnswer"
                                    value="no"
                                    checked={redirectAnswer === false}
                                    onChange={() => setRedirectAnswer(false)}
                                />
                                <label className="radio" htmlFor="radio-no" style={{ width: "50%", margin: "4px" }}>
                                    <span>No</span>
                                </label>
                            </div>
                        </fieldset>
                    </form>
                </div>

            )}
            <div className="quote-details">
                <div>
                    <label className="text" htmlFor={'quoteOutcome-sidebar'}>Quote Outcome</label>
                    <QuoteOutcome
                        id="quoteOutcome-sidebar"
                        selectedOutcome={selectedOutcome}
                        setSelectedOutcome={setSelectedOutcome}
                        handleInput={handleInput}
                        highlightedFields={highlightedFields}
                    />
                </div>
                {(shouldTypeOfQuoteDropwdownsShow) && (
                    <div>
                        {(selectedOutcome !== "Sale" && selectedOutcome !== "Follow-Up") && (
                            <div>
                                <div>
                                    <label className="text" htmlFor={'primaryOutcomeType-sidebar'}>
                                        Type Of Quote
                                    </label>
                                </div>
                                <div>
                                    <div className="select-box">
                                        <select
                                            id="primaryOutcomeType-sidebar"
                                            name="primaryOutcomeType"
                                            value={upsertQuoteInputs.primaryOutcomeType}
                                            onChange={handleInput}
                                            style={{ borderColor: highlightedFields.primaryOutcomeType ? 'red' : undefined }}
                                        >
                                            <option value="">Select</option>
                                            {typeOfQuote.map((quoteType, index) => (
                                                <option key={index} value={quoteType}>
                                                    {quoteType}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {upsertQuoteInputs.primaryOutcomeType === 'Misdirect' && (
                            <div>
                                <label className="text" htmlFor={'secondaryOutcomeType-sidebar'}>Subtype</label>
                                <div>
                                    <div className="select-box">
                                        <select
                                            id="secondaryOutcomeType-sidebar"
                                            name="secondaryOutcomeType"
                                            value={upsertQuoteInputs.secondaryOutcomeType}
                                            onChange={handleInput}
                                            style={{ borderColor: highlightedFields.secondaryOutcomeType ? 'red' : undefined }}
                                        >
                                            <option value="">Select</option>
                                            {subtype.map((quoteSecondaryType, index) => (
                                                <option key={index} value={quoteSecondaryType}>
                                                    {quoteSecondaryType}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>)}
                <div className="partner-quote-field-container">
                    <label className="text" htmlFor={'zipCode-sidebar'}>5-Digit Zip Code</label>
                    <div><input type="text"
                        id='zipCode-sidebar'
                        name="zipCode"
                        value={upsertQuoteInputs.zipCode}
                        onChange={handleInput}
                        style={{
                            width: isExpandedMore ? '70%' : '',
                            borderColor: highlightedFields.zipCode ? 'red' : undefined
                        }}
                        disabled={redirectAnswer === true}
                    />
                    </div>
                    {validationErrors.zipCode && <div className="error-message">{validationErrors.zipCode}</div>}
                </div>
                <div>
                    <label className="text" htmlFor={'stateCode-sidebar'}>State Code</label>
                    <div><input type="text"
                        id="stateCode-sidebar"
                        name="stateCode"
                        value={upsertQuoteInputs.stateCode}
                        onChange={handleInput}
                        disabled={true} /></div>
                </div>
                <div className="partner-quote-field-container">
                    <label className="text" htmlFor={'emailId-sidebar'}>Email</label>
                    <div><input type="text"
                        id="emailId-sidebar"
                        name="emailId"
                        value={upsertQuoteInputs.emailId}
                        onChange={handleInput}
                        style={{
                            width: isExpandedMore ? '70%' : '',
                            borderColor: highlightedFields.emailId ? 'red' : undefined
                        }}
                    />
                    </div>
                    {validationErrors.emailId && <div className="error-message">{validationErrors.emailId}</div>}
                </div>
                <div className="partner-quote-field-container">
                    <label className="text" htmlFor={'phoneNumber-sidebar'}>Phone Number</label>
                    <div>
                        <InputMask
                            mask="999-999-9999"
                            value={upsertQuoteInputs.phoneNumber}
                            onChange={handleInput}
                        >
                            {(inputProps: any) => (
                                <input
                                    {...inputProps}
                                    id="phoneNumber-sidebar"
                                    type="text"
                                    name="phoneNumber"
                                    placeholder="XXX-XXX-XXXX"
                                    style={{ borderColor: highlightedFields.phoneNumber ? 'red' : undefined }}
                                />
                            )}
                        </InputMask>
                    </div>
                    {validationErrors.phoneNumber && <div className="error-message">{validationErrors.phoneNumber}</div>}
                </div>
                <div className="partner-quote-field-container">
                    <label className="text" htmlFor={'firstName-sidebar'}>First Name</label>
                    <div><input type="text"
                        id="firstName-sidebar"
                        name="firstName"
                        value={upsertQuoteInputs.firstName}
                        onChange={handleInput}
                        style={{
                            width: isExpandedMore ? '70%' : '',
                            borderColor: highlightedFields.firstName ? 'red' : undefined
                        }}
                    />
                        {validationErrors.firstName && <div className="error-message">{validationErrors.firstName}</div>}
                    </div>
                </div>
                <div className="partner-quote-field-container">
                    <label className="text" htmlFor={'lastname-sidebar'}>Last Name</label>
                    <div><input type="text"
                        id="lastName-sidebar"
                        name="lastName"
                        value={upsertQuoteInputs.lastName}
                        onChange={handleInput}
                        style={{
                            width: isExpandedMore ? '70%' : '',
                            borderColor: highlightedFields.lastName ? 'red' : undefined
                        }} />
                    </div>
                    {validationErrors.lastName && <div className="error-message">{validationErrors.lastName}</div>}
                </div>
                <div className="partner-quote-field-container">
                    <label className="text" htmlFor={'dateOfBirth-sidebar'}>Date of Birth</label>
                    <div>
                        <InputMask
                            mask="99/99/9999"
                            value={upsertQuoteInputs.dateOfBirth}
                            onChange={handleInput}
                        >
                            {(inputProps: any) => (
                                <input
                                    {...inputProps}
                                    id="dateOfBirth-sidebar"
                                    type="text"
                                    name="dateOfBirth"
                                    placeholder="MM/DD/YYYY"
                                    style={{ borderColor: highlightedFields.dateOfBirth ? 'red' : undefined }}
                                />
                            )}
                        </InputMask>
                    </div>
                    {validationErrors.dateOfBirth && <div className="error-message">{validationErrors.dateOfBirth}</div>}
                </div>

                <div>
                    <QuoteOutcomeFields
                        selectedOutcome={selectedOutcome}
                        upsertQuoteInputs={upsertQuoteInputs}
                        validationErrors={validationErrors}
                        handleInput={handleInput}
                        lineOfBusinessOptions={lobNames}
                        leadSourceOptions={leadSources}
                        languageOptions={languages}
                        setRequiredFields={setRequiredFields}
                        resetQuoteOutcomeFields={resetQuoteOutcomeFields}
                        isFixedSource={isFixedSource}
                        redirectAnswer={redirectAnswer}
                        carrierCodes={carrierCodes}
                        isRPMAuthorization={isRPMAuthorization}
                        highlightedFields={highlightedFields}
                        setHighlightedFields={setHighlightedFields}
                        referenceNumber={upsertQuoteInputs.referenceNumber || upsertQuoteInputs.policyNumber || ""}
                        endorsementLobs={endorsements
                            .filter(e => e.lob && e.lob.trim() !== "")
                            .map(e => e.lob)}
                        isPrefillMode={isPrefillMode}
                        endorsementLobNames={endorsementLobNames}
                        onEndorsementDataChange={(hasEndorsements, endorsements) => {
                            setHasEndorsements(hasEndorsements);
                            setEndorsements(endorsements);
                        }}
                    />
                </div>
                {(upsertQuoteInputs.lineOfBusiness) && <div>
                    <Alert
                        title={alertMessageDisclosure.title}
                        content={alertMessageDisclosure.message}
                        alertImportance="low"
                    />
                </div>}
                <div className="partner-quote-field-container">
                    <label htmlFor="agentNotes-sidebar" className="text">Notes</label>
                    <textarea
                        id="agentNotes-sidebar"
                        name="agentNotes"
                        value={manualNotes}
                        onChange={handleNotesChange}
                        style={{ width: "100%", borderColor: highlightedFields.agentNotes ? 'red' : undefined }}
                    />
                </div>
                <div className="partner-quote-field-container">
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <label className="text" htmlFor="disclosures-readonly-sidebar">Disclosures and Risk Details</label>
                        <span
                            className="geico-icon icon-copy geico-icon--informational geico-icon--xsmall copy-button"
                            data-keyword="copy paste clipboard"
                            role="button"
                            tabIndex={0}
                            onClick={handleCopyDisclosures}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCopyDisclosures(); } }}
                            aria-label="Copy disclosures to clipboard"
                            title="Copy disclosures to clipboard"
                            style={{ cursor: "pointer" }}
                        ></span>
                        {disclosuresCopied && <span style={{ fontSize: "0.75rem", color: "green" }}>Copied!</span>}
                    </div>
                    <textarea
                        id="disclosures-readonly-sidebar"
                        readOnly
                        value={autoNotes}
                        style={{ width: "100%", background: "#f5f5f5", color: "#555", cursor: "default" }}
                    />
                </div>
                {successMessage && (
                    <div className="alert-success">
                        {successMessage}
                    </div>
                )}
                {(showAlert && (alertMessage.title || alertMessage.message) &&
                    <div className="alert-failure">
                        {alertMessage.message}
                    </div>
                )}
                <div style={{ display: 'flex' }}>
                    <Button className="btn--primary nav-button-back mt-10 mr-2" children="Save Quote " onClick={(e) => submitForm()}></Button>
                    <Button className="btn--secondary nav-button-back mt-10 mr-2" children="   Reset   " onClick={(e) => { resetForm() }}></Button>
                    <Button className="btn--secondary nav-button-back mt-10" children="Create Copy" onClick={(e) => { createCopy() }}></Button>
                </div>
            </div>
        </div >
    );
};